import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, LogOut, Scissors, ExternalLink, Eye } from 'lucide-react';
import { adminMe, setAuthToken } from '../lib/api';
import AdminClippersTab from '../components/admin/AdminClippersTab';
import { Button } from '../components/ui/button';

const SITE_LOGO =
  'https://customer-assets.emergentagent.com/job_87cd0f70-4f18-4348-a2fd-e2666a61734d/artifacts/ozrawt0t_IMG_0439.jpeg';

/**
 * Standalone, full-screen admin page for the Clip Program.
 * No sidebar — clean focused workspace just for managing
 * clippers, clips, and withdrawals.
 *
 * Route: /admin/clippers (admin auth required)
 */
export default function AdminClippersPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminMe();
        setMe(r);
      } catch (e) {
        if (e?.response?.status === 401) {
          setAuthToken(null);
          navigate('/admin/nayemur/alvee/login', { replace: true });
          return;
        }
        toast.error('Failed to load admin');
      } finally { setLoading(false); }
    })();
  }, [navigate]);

  function logout() {
    setAuthToken(null);
    navigate('/admin/nayemur/alvee/login', { replace: true });
  }

  if (loading || !me) {
    return (
      <main className="min-h-screen bg-[hsl(0_0%_4%)] text-white flex items-center justify-center">
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Loading admin…</div>
      </main>
    );
  }

  return (
    <main className="min-h-screen text-white bg-[hsl(0_0%_4%)] relative noise-overlay" data-testid="admin-clippers-page">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 opacity-40"
        style={{
          background:
            'radial-gradient(60% 60% at 0% 0%, rgba(245,185,66,0.10), transparent 70%), radial-gradient(40% 40% at 100% 100%, rgba(225,29,46,0.08), transparent 70%)',
        }}
      />
      {/* Top bar */}
      <header className="relative z-10 border-b border-white/5 backdrop-blur-md bg-[hsla(0,0%,4%,0.72)] sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <img src={SITE_LOGO} alt="AlveeFx" className="h-8 w-8 rounded-full object-cover ring-1 ring-[rgba(245,185,66,0.42)]" />
            <div className="min-w-0">
              <div className="font-display uppercase tracking-[0.18em] text-sm flex items-center gap-2">
                <span>AlveeFx</span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[hsl(43_92%_72%)] px-2 py-0.5 rounded-full border border-[hsl(43_92%_56%)]/40 bg-[rgba(245,185,66,0.06)] inline-flex items-center gap-1">
                  <Scissors className="h-3 w-3" /> Clipper Console
                </span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground truncate">{me.email || 'admin'}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/admin/nayemur/alvee" data-testid="open-dashboard">
              <Button variant="outline" size="sm" className="h-9 border-white/10 bg-white/[0.02] gap-1.5">
                <ArrowLeft className="h-3.5 w-3.5" /> Main dashboard
              </Button>
            </Link>
            <a href="/clip" target="_blank" rel="noreferrer" data-testid="open-public">
              <Button variant="outline" size="sm" className="h-9 border-white/10 bg-white/[0.02] gap-1.5">
                <Eye className="h-3.5 w-3.5" /> Public page <ExternalLink className="h-3 w-3 opacity-50" />
              </Button>
            </a>
            <Button variant="outline" size="sm" onClick={logout} className="h-9 border-white/10 bg-white/[0.02] gap-1.5" data-testid="admin-logout">
              <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </div>
      </header>

      <section className="relative z-10 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AdminClippersTab />
        </div>
      </section>
    </main>
  );
}
